// @ts-nocheck
import { useEffect, useRef, useState, useCallback } from "react";
import { Sparkles, Send, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useBeforeUnload } from "@/hooks/useBeforeUnload";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface ChatTurn {
  role: "assistant" | "user";
  text: string;
}

interface AIEventBuilderChatProps {
  /** Called when the assistant finishes — receives the collected event object */
  onComplete: (event: any) => void;
}

const STORAGE_KEY = "ai-event-builder-draft";

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        transcript: Array.isArray(parsed.transcript) ? parsed.transcript : [],
      };
    }
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return null;
}

function saveDraft(messages: any[], transcript: ChatTurn[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, transcript }));
  } catch {
    /* noop */
  }
}

function clearDraft() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export default function AIEventBuilderChat({ onComplete }: AIEventBuilderChatProps) {
  const draft = loadDraft();
  const [messages, setMessages] = useState<any[]>(draft?.messages ?? []);
  const [transcript, setTranscript] = useState<ChatTurn[]>(draft?.transcript ?? []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupError, setSetupError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist draft on every update
  useEffect(() => {
    if (messages.length > 0 || transcript.length > 0) {
      saveDraft(messages, transcript);
    }
  }, [messages, transcript]);

  // Warn before closing the tab while a conversation is in progress
  useBeforeUnload({
    enabled: transcript.length > 0,
    message: "You have an event in progress — leave anyway?",
  });

  // Block in-app navigation (sidebar Links etc.) while a conversation is active.
  // useBlocker can't be used here because the app mounts <BrowserRouter> (not a
  // data router), so we intercept link clicks during the capture phase instead.
  const hasConversation = transcript.length > 0;
  useEffect(() => {
    if (!hasConversation) return;

    const handleClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const anchor = (e.target as HTMLElement)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      // Ignore non-navigations, new tabs, and same-page anchors
      if (!href || href.startsWith("#") || anchor.target === "_blank") return;
      // Stay if the link points to the current location
      if (anchor.href === window.location.href) return;

      const leave = window.confirm("You have an event in progress. Leave and lose it?");
      if (!leave) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("click", handleClickCapture, true);
    return () => document.removeEventListener("click", handleClickCapture, true);
  }, [hasConversation]);

  const send = useCallback(async (userText: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeError } = await invokeEdgeFunction(
        "rle-ai-event-builder",
        { body: { messages, user_message: userText } }
      );

      if (invokeError) {
        const ctx = (invokeError as any).context;
        let status = ctx?.status;
        let payload: any = null;
        try {
          payload = await ctx?.json?.();
        } catch {
          /* noop */
        }
        if (status === 503 || /ANTHROPIC_API_KEY/i.test(invokeError.message ?? "")) {
          setSetupError(true);
          return;
        }
        throw new Error(payload?.error || invokeError.message);
      }

      if (data?.error && /ANTHROPIC_API_KEY/i.test(data.error)) {
        setSetupError(true);
        return;
      }
      if (data?.error) throw new Error(data.error);

      setMessages(data.messages ?? []);
      if (data.assistant_message) {
        setTranscript((t) => [
          ...t,
          { role: "assistant", text: data.assistant_message },
        ]);
      }
      if (data.done) {
        clearDraft();
        onComplete(data.event ?? {});
      }
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [messages, onComplete]);

  // Kick off the conversation once (only if no saved draft)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!draft) {
      send("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [transcript, loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setTranscript((t) => [...t, { role: "user", text }]);
    setInput("");
    send(text);
  };

  const handleStartOver = () => {
    clearDraft();
    setMessages([]);
    setTranscript([]);
    setError(null);
    setInput("");
    send("");
  };

  if (setupError) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-4">
        <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
        <h3 className="text-lg font-bold text-foreground">AI setup isn't enabled yet</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          The AI event builder needs to be configured before you can use it. In
          the meantime, you can clone a previous event or build one from scratch.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[420px]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-bold text-foreground">AI Event Builder</h3>
        </div>
        {(messages.length > 0 || transcript.length > 0) && (
          <button
            onClick={handleStartOver}
            disabled={loading}
            className="text-xs font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 disabled:opacity-50 transition-colors"
            title="Start over"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Start over
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 pr-1"
      >
        {transcript.map((turn, i) => (
          <div
            key={i}
            className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                turn.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              {turn.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted text-muted-foreground rounded-2xl px-4 py-2.5 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
        {error && (
          <div className="text-sm text-destructive px-1">{error}</div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-3 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          placeholder="Type your answer…"
          className="flex-1 rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="bg-primary text-primary-foreground rounded-lg p-2.5 hover:opacity-90 transition-opacity disabled:opacity-50"
          aria-label="Send"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}
